function formatEmbroideryMoney(cents) {
  const settings = (window.themeVariables && window.themeVariables.settings) || {};
  const format = (settings.currencyCodeEnabled && settings.moneyWithCurrencyFormat) || settings.moneyFormat || '${{amount}}';
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number == null) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const remainder = parts[1] ? decimal + parts[1] : '';
    return dollars + remainder;
  }

  let value = '';
  const match = format.match(placeholder);
  switch (match ? match[1] : 'amount') {
    case 'amount': value = withDelimiters(cents, 2, ',', '.'); break;
    case 'amount_no_decimals': value = withDelimiters(cents, 0, ',', '.'); break;
    case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
    case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
    case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
    case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
    default: value = withDelimiters(cents, 2, ',', '.');
  }

  return format.replace(placeholder, value);
}

function updateEmbroideryPrices(scope) {
  if (!scope) return;

  let surcharge = 0;
  scope.querySelectorAll('custom-embroidery').forEach(el => {
    if (typeof el.getSurcharge === 'function') surcharge += el.getSurcharge();
  });

  scope.querySelectorAll('[data-embroidery-price]').forEach(node => {
    if (node.dataset.embroideryOrig == null) {
      node.dataset.embroideryOrig = node.textContent.trim();
    }
    const base = parseInt(node.dataset.embroideryBase, 10);
    if (surcharge > 0 && !isNaN(base)) {
      node.textContent = formatEmbroideryMoney(base + surcharge);
    } else {
      node.textContent = node.dataset.embroideryOrig;
    }
  });
}

class CustomEmbroidery extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.custom-embroidery__toggle');
    this.options = this.querySelector('.custom-embroidery__options');
    this.fields = this.querySelectorAll('input, textarea');
    this.colorInputs = this.querySelectorAll('.custom-embroidery__swatches .custom-embroidery__swatch-input');
    this.colorNameEl = this.querySelector('.custom-embroidery__selected-color-name');
    this.associatedPrice = parseInt(this.dataset.associatedPrice, 10) || 0;
    this.section = this.closest('.shopify-section');
    this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    this.toggle.addEventListener('click', () => this.handleToggle());
    this.colorInputs.forEach(el => el.addEventListener('change', () => this.updateColorName()));

    this.addEventListener('change', () => this.updatePrice());

    if (this.form) {
      this.form.addEventListener('variant:change', () => this.updatePrice());
    }

    // Embroidery starts hidden, so make sure its properties are not submitted.
    this.setActive(false);
  }

  handleToggle() {
    this.setActive(this.options.hasAttribute('hidden'));
  }

  setActive(active) {
    // Tracks whether the associated add-on product should be added to the cart.
    this.active = active;

    if (active) {
      this.options.removeAttribute('hidden');
      this.toggle.textContent = this.toggle.dataset.removeLabel;
    } else {
      this.options.setAttribute('hidden', '');
      this.toggle.textContent = this.toggle.dataset.addLabel;
      this.clearFields();
    }

    this.toggle.setAttribute('aria-expanded', active ? 'true' : 'false');

    // Disabled fields are excluded from the product form submission, so the
    // embroidery properties are only added to the cart when embroidery is on.
    this.fields.forEach(el => { el.disabled = !active; });

    this.updateColorName();
    this.updatePrice();
  }

  getSurcharge() {
    if (!this.active) return 0;
    const selectedIcon = this.querySelector('input[name="properties[Icon Embroidery Icon]"]:checked');
    if (selectedIcon && selectedIcon.dataset.variantPrice) {
      return parseInt(selectedIcon.dataset.variantPrice, 10) || 0;
    }
    return this.associatedPrice;
  }

  updatePrice() {
    updateEmbroideryPrices(this.section);
  }

  clearFields() {
    this.querySelectorAll('input[type="text"], textarea').forEach(el => { el.value = ''; });
    this.querySelectorAll('input[type="radio"]').forEach(el => { el.checked = false; });
    this.querySelectorAll('input[data-default]').forEach(el => { el.checked = true; });
  }

  updateColorName() {
    if (!this.colorNameEl) return;
    const checked = Array.from(this.colorInputs).find(el => el.checked);
    if (checked) this.colorNameEl.textContent = checked.value;
  }

  // Returns the add-on product to add to the cart, or null when embroidery is
  // off or no associated product has been configured in the theme editor.
  getAssociatedItem() {
    if (!this.active) return null;
    let variantId = this.dataset.associatedVariantId;
    // For icon embroidery, add the add-on variant whose name matches the selected
    // icon; fall back to the default variant when no matching variant exists.
    const selectedIcon = this.querySelector('input[name="properties[Icon Embroidery Icon]"]:checked');
    if (selectedIcon && selectedIcon.dataset.variantId) {
      variantId = selectedIcon.dataset.variantId;
    }
    if (!variantId) return null;
    return { id: variantId, quantity: 1, properties: this.getCustomizationProperties() };
  }

  // Collects this block's line-item properties (e.g. Embroidery Text, Embroidery
  // Color, Font Style) so they can be mirrored onto the add-on product line.
  getCustomizationProperties() {
    const properties = {};
    this.querySelectorAll('[name^="properties["]').forEach(input => {
      if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      const key = match[1];
      if (key.charAt(0) === '_') return;
      if (input.value.trim() === '') return;
      properties[key] = input.value;
    });
    return properties;
  }
}

if (!window.customElements.get('custom-embroidery')) {
  window.customElements.define('custom-embroidery', CustomEmbroidery);
}

// Adds the configured add-on product(s) to the cart whenever a product form is
// submitted with an embroidery option enabled. Runs in the capture phase so the
// add-on lands in the cart before the theme adds the main product — this keeps
// the cart drawer render (and the page-cart redirect) correct with no flicker.
if (!window.__customEmbroideryAssociatedProduct) {
  window.__customEmbroideryAssociatedProduct = true;

  document.addEventListener('submit', async function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;

    // Second, programmatic submit: let the theme add the main product.
    if (form.dataset.embroideryBypass === '1') {
      delete form.dataset.embroideryBypass;
      return;
    }

    // Guard against a double submit while the add-on request is in flight.
    if (form.dataset.embroideryPending === '1') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // Remove any stale link input left in the form by a previous submit, so a
    // subsequent non-embroidery add never inherits an old token.
    const staleLink = form.querySelector('input[data-embroidery-link]');
    if (staleLink) staleLink.remove();

    // Collect add-on items from every active embroidery block tied to this form.
    // Note: use getAttribute('id') — `form.id` is shadowed by the form's own
    // <input name="id"> control and would return that element, not the id string.
    const formId = form.getAttribute('id');
    const items = [];
    document.querySelectorAll('custom-embroidery').forEach(el => {
      if (el.dataset.formId && formId && el.dataset.formId !== formId) return;
      const item = typeof el.getAssociatedItem === 'function' ? el.getAssociatedItem() : null;
      if (item) items.push(item);
    });

    if (!items.length) return;

    // Match the add-on quantity to the main product quantity.
    const qtyInput = form.querySelector('[name="quantity"]');
    const quantity = Math.max(parseInt(qtyInput && qtyInput.value, 10) || 1, 1);

    // A unique token links the add-on line(s) to the main product line so the
    // cart can remove them together (see embroidery-cart-link.js). Add-on lines
    // are also flagged so the reconciler can tell parent from add-on.
    const linkToken = 'emb-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    items.forEach(item => {
      item.quantity = quantity;
      item.properties = Object.assign({ _embroidery_link: linkToken, _embroidery_addon: '1' }, item.properties);
    });

    event.preventDefault();
    event.stopImmediatePropagation();
    form.dataset.embroideryPending = '1';

    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    let addonAdded = false;
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });
      addonAdded = res.ok;
    } catch (e) {
      console.error('[custom-embroidery] Failed to add associated product', e);
    }

    // Only tag the main product line when the add-on actually made it into the
    // cart, so a standalone product is never orphan-removed by the reconciler.
    if (addonAdded) {
      const linkInput = document.createElement('input');
      linkInput.type = 'hidden';
      linkInput.name = 'properties[_embroidery_link]';
      linkInput.value = linkToken;
      linkInput.setAttribute('data-embroidery-link', '1');
      form.appendChild(linkInput);
    }

    delete form.dataset.embroideryPending;

    // Re-submit so the theme's own handler adds the main product. The bypass
    // flag lets that submit through untouched.
    form.dataset.embroideryBypass = '1';
    if (typeof form.requestSubmit === 'function') {
      if (event.submitter) {
        form.requestSubmit(event.submitter);
      } else {
        form.requestSubmit();
      }
    } else {
      form.submit();
    }
  }, true);
}
